import jwtDecode from "jwt-decode";
import Axios from "../config/axios-v1-config";
import { AuthService } from "../services/authservice";

/**
 * Cliente para el perfil "admin de holding".
 *
 * IMPORTANTE: este módulo NO consume ningún endpoint exclusivo `/holding/*`
 * del bff. El holding es un tipo de usuario más que reutiliza los
 * microservicios existentes; cada microservicio detecta `role.type === 'holding'`
 * vía JWT y aplica el filtro server-side por `holding.companies[]`.
 *
 *   - `getHoldingMe()`           → lee del JWT (sin llamada de red)
 *   - `getHoldingCompanies()`    → microservice-contracts
 *   - `createHoldingCompany()`   → microservice-contracts
 *   - `getHoldingPackages()`     → microservice-packages (`/packages`)
 *   - `getHoldingPickups()`      → microservice-pickups (`/pickups`)
 */

/**
 * Datos del holding del usuario logueado leídos directamente del JWT.
 * No hace request HTTP — el JWT ya viaja con `holding` populado
 * (ver `bff-auth-gateway/src/api/auth/auth.service.ts payload()`).
 */
export const getHoldingMe = () => {
  const token = AuthService.getAccessTokenApi();
  if (!token) return null;
  try {
    const decoded = jwtDecode(token);
    return decoded?.holding ?? null;
  } catch (e) {
    return null;
  }
};

// Lista paginada de empresas hijas del holding. El filtro por holdingId lo
// aplica `microservice-contracts` server-side leyendo el JWT.
export const getHoldingCompanies = async ({ page = 1, limit = 10, q } = {}) => {
  try {
    const params = new URLSearchParams();
    params.append("page", page);
    params.append("limit", limit);
    if (q && q.trim()) params.append("q", q.trim());
    const response = await Axios.get(`contracts/holding-companies?${params.toString()}`);
    return response.data;
  } catch (err) {
    return { error: err.response?.data || "Error obteniendo empresas." };
  }
};

// Detalle de una empresa hija. microservice-contracts valida que la company
// pertenezca al holding del JWT antes de devolverla.
export const getHoldingCompany = async (id) => {
  try {
    const response = await Axios.get(`contracts/holding-companies/${id}`);
    return response.data;
  } catch (err) {
    return { error: err.response?.data || "Error obteniendo la empresa." };
  }
};

// Crea una empresa hija. El `businessIdentifier` lo genera microservice-contracts
// con $inc atómico sobre `Holding.nextChildCounter` — el cliente NO debe enviarlo.
export const createHoldingCompany = async (data) => {
  try {
    const response = await Axios.post("contracts/holding-companies", data);
    return response.data;
  } catch (err) {
    return { error: err.response?.data || "Error creando empresa." };
  }
};

// Edita una empresa hija. microservice-contracts ignora cualquier intento
// de modificar `businessIdentifier` o `holdingId` desde un caller de tipo holding.
export const updateHoldingCompany = async (id, data) => {
  try {
    const response = await Axios.patch(`contracts/holding-companies/${id}`, data);
    return response.data;
  } catch (err) {
    return { error: err.response?.data || "Error actualizando empresa." };
  }
};

// Paquetes consolidados: `microservice-packages` detecta el rol holding y
// filtra automáticamente por `senderInfo.companyId ∈ holding.companies[]`.
// Contrato de params alineado con `getAdminPackages` para que la vista
// `HoldingResume` pueda reusar la misma estructura del modal de filtros.
// `companyId` opcional limita a una empresa hija puntual (server valida que
// esté dentro del scope).
export const getHoldingPackages = async ({
  page = 1,
  limit = 25,
  query,
  orderField = "updatedAt",
  sortType = "desc",
  coms,
  // Código postal declarado (CPA o CP4). Mismo contrato que `getAdminPackages`.
  zipCode,
  status,
  startDate,
  endDate,
  companyId,
  updatedAtSince,
} = {}) => {
  try {
    const params = new URLSearchParams({
      page,
      limit,
      orderField,
      sortType,
      ...(query?.query && { [query.type]: query.query }),
      ...(coms && { "shipping_address.municipality.name": coms }),
      ...(zipCode && { "shipping_address.zip_code": zipCode }),
      ...(startDate && { startDateCreation: startDate, endDateCreation: endDate }),
      ...(updatedAtSince && { updatedAtSince }),
      ...(companyId && { companyId }),
    });

    if (status !== undefined && status !== "") {
      if (Array.isArray(status)) {
        status.forEach((s) => params.append("status", s));
      } else {
        params.append("status", status);
      }
    }

    const response = await Axios.get(`packages?${params.toString()}`);
    return { success: true, data: response.data, error: null };
  } catch (err) {
    return { success: false, data: null, error: err.response?.data || "Error obteniendo paquetes." };
  }
};

// Autocomplete de senders (companyintegrations.integrations.sender_id) limitado
// al scope del holding. Devuelve `[{ companyId, companyName, sender_id, integrationName }]`.
// `q` se compara contra el sender_id con regex insensitive (microservice-contracts).
export const searchHoldingSenders = async (q = "", limit = 20) => {
  try {
    const params = new URLSearchParams();
    if (q) params.append("q", q);
    if (limit) params.append("limit", limit);
    const response = await Axios.get(`contracts/holding-senders/search?${params.toString()}`);
    return response.data;
  } catch (err) {
    return { error: err.response?.data || "Error buscando senders." };
  }
};

// Pickups consolidados en un rango de fechas. microservice-pickups detecta el
// rol holding y fuerza el filtro `client ∈ holding.companies[]`.
// Requiere `startDate` y `endDate` en ISO 8601. `companyId` (opcional) limita
// a una empresa hija puntual (debe estar dentro del scope del holding).
export const getHoldingPickups = async ({ startDate, endDate, companyId } = {}) => {
  try {
    const params = new URLSearchParams();
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    if (companyId) params.append("client", companyId);
    const response = await Axios.get(`pickups/date-range?${params.toString()}`);
    return response.data;
  } catch (err) {
    return { error: err.response?.data || "Error obteniendo pickups." };
  }
};
